import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { selectSessionExpired } from 'common/selectors';
import T from 'i18n';
import api from 'common/api';
import Logger from 'utils/logger';
import { SESSION_EXPIRED_STORAGE_KEY, SESSION_EXPIRED_RETURN_TO_KEY } from './constants';
import { redirectToLoginStartPage } from 'utils/path';

const removeSessionStorageFlag = () => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY);
    }
  } catch (e) {
    // Ignore storage errors (e.g. private browsing mode).
  }
};

const getStoredSessionExpiredFlag = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (!window.sessionStorage) return false;
    return window.sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const redirectToLogin = () => {
  removeSessionStorageFlag();
  redirectToLoginStartPage();
};

export default function SessionExpiredOverlay() {
  const sessionExpired = useSelector(selectSessionExpired);

  useEffect(() => {
    if (!sessionExpired) {
      const storedFlag = getStoredSessionExpiredFlag();
      if (storedFlag) {
        try {
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(
              SESSION_EXPIRED_RETURN_TO_KEY,
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
            );
          }
        } catch (e) {
          // Ignore storage errors so we can still proceed.
        }
        redirectToLogin();
      }
    }
  }, [sessionExpired]);

  const handleSignInAgain = useCallback(() => {
    try {
      api.post('/auth/logout', {}, { keepalive: true }).catch((e) => {
        Logger.WARN && Logger.WARN('Server logout failed (continuing):', e);
      });
    } catch (e) {
      Logger.ERROR(e, 'Unexpected error during server logout from overlay');
    }
    redirectToLogin();
  }, []);

  const handleDialogClose = useCallback((event, reason) => {
    if (reason === 'backdropClick') return;
  }, []);

  if (!sessionExpired) return null;

  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      onClose={handleDialogClose}
      disableEscapeKeyDown
      keepMounted
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
        },
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 5, px: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {T.translate('auth.sessionExpiredTitle')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          {T.translate('auth.sessionExpiredMessage')}
        </Typography>
        <DialogActions sx={{ justifyContent: 'center', px: 0 }}>
          <Button variant="contained" color="primary" onClick={handleSignInAgain}>
            {T.translate('auth.sessionExpiredCta')}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
